/**
 * Offline-capable session management.
 *
 * Wraps Firebase `onAuthStateChanged` to cache the latest user in memory and
 * expose a simple subscription API.  Auth persistence is set to
 * `browserLocalPersistence` so the session survives page reloads.
 */

import {
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  type User,
} from 'firebase/auth';
import { auth } from '$lib/firebase/client';

let currentUser: User | null = null;
let initialized = false;
let authStateKnown = false;

const listeners = new Set<(user: User | null) => void>();
let authStateWaiters: Array<(user: User | null) => void> = [];

let proxyCache = new WeakMap<any, User>();
let cachedImpersonatedUid: string | null = null;

function wrapUser(user: User | null): User | null {
  if (!user) return null;
  if (typeof window === 'undefined') return user;

  const impersonatedUid = sessionStorage.getItem('impersonatedUid');
  if (!impersonatedUid) {
    cachedImpersonatedUid = null;
    return user;
  }

  if (cachedImpersonatedUid !== impersonatedUid) {
    cachedImpersonatedUid = impersonatedUid;
    proxyCache = new WeakMap<any, User>();
  } else {
    const cached = proxyCache.get(user);
    if (cached) return cached;
  }

  const proxied = new Proxy(user, {
    get(target, prop, receiver) {
      if (prop === 'uid') {
        return impersonatedUid;
      }
      if (prop === 'originalUid') {
        return target.uid;
      }
      const value = Reflect.get(target, prop, receiver);
      if (typeof value === 'function') {
        return value.bind(target);
      }
      return value;
    }
  }) as unknown as User;

  proxyCache.set(user, proxied);
  return proxied;
}

/**
 * Initialise the auth provider.
 *
 * Sets local persistence and subscribes to Firebase auth state. Safe to call
 * multiple times — subsequent calls are no-ops.
 */
export function initAuthProvider(): void {
  if (initialized) return;
  if (!auth) {
    console.warn('Auth provider: Firebase Auth not initialised yet — deferring.');
    return;
  }
  initialized = true;

  // Set persistence so tokens survive page reload
  setPersistence(auth!, browserLocalPersistence).catch((err) =>
    console.warn('Auth persistence setup failed:', err),
  );

  onAuthStateChanged(auth!, (user) => {
    currentUser = user;
    authStateKnown = true;
    const wrapped = wrapUser(user);

    const waiters = authStateWaiters;
    authStateWaiters = [];
    for (const resolve of waiters) {
      resolve(wrapped);
    }

    for (const fn of listeners) {
      try {
        fn(wrapped);
      } catch (err) {
        console.warn('Auth listener error:', err);
      }
    }
  });
}

/**
 * Resolves with the Firebase user (or null) once the first auth event has
 * been emitted. Does not treat a pre-Firebase null as signed-out.
 */
export function waitForAuthState(): Promise<User | null> {
  if (authStateKnown) {
    return Promise.resolve(wrapUser(currentUser));
  }
  return new Promise((resolve) => {
    authStateWaiters.push(resolve);
  });
}

/** Test-only: reset module state so each spec starts from a clean provider. */
export function resetAuthProviderForTests(): void {
  currentUser = null;
  initialized = false;
  authStateKnown = false;
  listeners.clear();
  authStateWaiters = [];
  proxyCache = new WeakMap();
  cachedImpersonatedUid = null;
}

/**
 * Returns the most recently emitted Firebase user, or `null` if no user is
 * signed in (or the provider hasn't emitted a value yet).
 */
export function getCachedUser(): User | null {
  return wrapUser(currentUser);
}

/**
 * Subscribe to auth state changes.
 *
 * @param callback — invoked with the current user (or null) on every change.
 * @returns a cleanup function that removes the listener.
 */
export function onAuthChange(callback: (user: User | null) => void): () => void {
  listeners.add(callback);

  if (authStateKnown) {
    callback(wrapUser(currentUser));
  }

  return () => {
    listeners.delete(callback);
  };
}
